import useSeo from "@/lib/useSeo";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import { ContactBlock } from "@/components/marketing/ContactBlock";
import api, { errMsg } from "@/lib/api";

const TOPICS = ["General enquiry", "Sales & pricing", "Consultancy partnerships", "Billing", "Privacy or legal", "Support"];
const TOPIC_PARAM = { sales: "Sales & pricing", partners: "Consultancy partnerships" };

export default function Contact() {
  useSeo({ title: "Contact us", description: "Questions about ChangeOS, tenders, accessibility or partnerships? Contact the ChangeOS team — we reply within one working day.", path: "/contact" });
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", topic: TOPIC_PARAM[params.get("topic")] || TOPICS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { const { data } = await api.post("/public/contact", form); setSent(data); }
    catch (ex) { setErr(errMsg(ex)); }
    finally { setBusy(false); }
  };

  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-16" data-testid="contact-page">
        <div className="pp-overline">Contact</div>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 max-w-3xl">Talk to a person, not a chatbot.</h1>
        <p className="text-lg text-subtle mt-5 max-w-2xl">Questions about pricing, a consultancy engagement, your data, or these terms — send a note and we'll reply by email, usually within two working days.</p>
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 mt-12 items-start">
          {sent ? (
            <div className="pp-card p-8" role="status" data-testid="contact-success">
              <div className="w-11 h-11 rounded-xl bg-success-100 text-success-700 flex items-center justify-center" aria-hidden="true"><CheckCircle2 size={22} /></div>
              <h2 className="font-head text-lg md:text-xl font-extrabold mt-4">Thanks — your message is on its way.</h2>
              <p className="text-sm text-subtle mt-2">Reference <span className="font-mono font-bold text-foreground">{sent.ref}</span>. We'll reply to <span className="font-semibold text-foreground">{form.email}</span>.{sent.email_status !== "sent" && " It's been logged for the team even though the email notification didn't go through."}</p>
              <Link to="/" className="pp-btn-ghost mt-6 inline-flex">Back to home</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="pp-card p-6 sm:p-8" aria-labelledby="contact-form-heading" data-testid="contact-form">
              <h2 id="contact-form-heading" className="font-head text-lg md:text-xl font-extrabold">Send us a message</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div><label htmlFor="contact-name" className="pp-label">Name</label><input id="contact-name" data-testid="contact-name" className="pp-input" value={form.name} onChange={set("name")} required maxLength={120} autoComplete="name" /></div>
                <div><label htmlFor="contact-email-input" className="pp-label">Email</label><input id="contact-email-input" data-testid="contact-email-input" type="email" className="pp-input" value={form.email} onChange={set("email")} required autoComplete="email" /></div>
              </div>
              <div className="mt-4"><label htmlFor="contact-topic" className="pp-label">Topic</label>
                <select id="contact-topic" data-testid="contact-topic" className="pp-input" value={form.topic} onChange={set("topic")}>{TOPICS.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div className="mt-4"><label htmlFor="contact-message" className="pp-label">Message</label>
                <textarea id="contact-message" data-testid="contact-message" className="pp-input min-h-[160px]" value={form.message} onChange={set("message")} required minLength={10} maxLength={4000} aria-describedby="contact-message-hint" />
                <div id="contact-message-hint" className="text-[11px] text-muted-foreground mt-1.5">{form.message.length}/4000 · please don't include passwords or card numbers.</div></div>
              {err && <div role="alert" data-testid="contact-error" className="mt-4 text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2">{err}</div>}
              <button data-testid="contact-submit" disabled={busy} className="pp-btn-dark mt-6">{busy ? "Sending…" : <>Send message <Send size={15} /></>}</button>
              <p className="text-[11px] text-muted-foreground mt-3">By sending this you agree to our <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>.</p>
            </form>
          )}
          <ContactBlock title="Or reach us directly" intro={null} showForm={false} stacked />
        </div>
      </section>
    </MarketingShell>
  );
}
